/**
 * Mosaic pixel -> PTZ from the grid metadata ACS returns with a mosaic.
 * Mirrors acs/src/pixelPointing.js so picks don't need a round-trip to the camera.
 */
import colorForLabel from '../../helpers/labelColor';
import {
  calculatePanTilt,
  isRowInRegion,
  setRowDict,
} from './acsSeatMath';

const REGION_COLORS = ['#5b9bd5', '#ed7d31', '#70ad47', '#ffc000', '#9e6bff', '#e74c3c', '#1abc9c'];

const ONVIF_MULTIPLIER = 1e9;

const wrapPan = (pan) => ((pan + 1.0) % 2.0) - 1.0;

const gridDims = (grid) => [
  Number(grid?.grid_cols) || 1,
  Number(grid?.grid_rows) || 1,
];

const isUsableGrid = (grid) => Boolean(
  grid?.width
  && grid.height
  && grid.k_pan_per_pixel != null
  && grid.k_tilt_per_pixel != null
  && Array.isArray(grid.cells)
  && grid.cells.length,
);

/** Mosaic pixel -> { ix, iy, lx, ly } inside a single captured frame. */
const mosaicToCell = (grid, mosaicX, mosaicY) => {
  const width = Number(grid.width);
  const height = Number(grid.height);
  const [cols, rows] = gridDims(grid);

  const ix = Math.min(cols - 1, Math.max(0, Math.floor(mosaicX / width)));
  const iy = Math.min(rows - 1, Math.max(0, Math.floor(mosaicY / height)));

  return {
    ix,
    iy,
    lx: mosaicX - ix * width,
    ly: mosaicY - iy * height,
  };
};

/** Absolute ONVIF pan/tilt for a mosaic pixel, or null if the grid can't be used. */
export const mosaicPixelToPtz = (grid, mosaicX, mosaicY) => {
  if (!isUsableGrid(grid)) return null;

  const {
    ix, iy, lx, ly,
  } = mosaicToCell(grid, mosaicX, mosaicY);
  const cell = grid.cells.find((c) => c.ix === ix && c.iy === iy);
  if (!cell) return null;

  const centerX = grid.center_x ?? Number(grid.width) / 2;
  const centerY = grid.center_y ?? Number(grid.height) / 2;
  const panScale = grid.pan_apply_scale ?? 1.0;

  const pan = wrapPan(
    Number(cell.pan) + Number(grid.k_pan_per_pixel) * panScale * (centerX - lx),
  );
  const tilt = Number(cell.tilt) + Number(grid.k_tilt_per_pixel) * (centerY - ly);

  return {
    pan, tilt, ix, iy, lx, ly, zoom: grid.zoom,
  };
};

/** ONVIF floats -> map-file integers relative to the camera reference point. */
export const ptzToMapRelative = (pan, tilt, reference) => ({
  panRel: Math.round(Number(pan) * ONVIF_MULTIPLIER) - Number(reference?.pan || 0),
  tiltRel: Math.round(Number(tilt) * ONVIF_MULTIPLIER) - Number(reference?.tilt || 0),
});

export const CORNER_ORDER = ['topLeft', 'topRight', 'bottomLeft', 'bottomRight'];

export const CORNER_DISPLAY = {
  topLeft: 'Top Left',
  topRight: 'Top Right',
  bottomLeft: 'Bottom Left',
  bottomRight: 'Bottom Right',
};

export const CORNER_SHORT = {
  topLeft: 'TL',
  topRight: 'TR',
  bottomLeft: 'BL',
  bottomRight: 'BR',
};

export const emptyMapGridCorner = () => ({
  pan: '',
  tilt: '',
  row: '',
  seat: '',
  mosaicX: null,
  mosaicY: null,
  tile_ix: null,
  tile_iy: null,
  tile_pan: null,
  tile_tilt: null,
  local_x: null,
  local_y: null,
});

/** Redux path updates that clear Map Grid corners + section editing selection. */
export const emptyMapGridEditingState = () => ({
  'mapGrid.topLeft': emptyMapGridCorner(),
  'mapGrid.topRight': emptyMapGridCorner(),
  'mapGrid.bottomLeft': emptyMapGridCorner(),
  'mapGrid.bottomRight': emptyMapGridCorner(),
  'mapGrid.activeCorner': 'topLeft',
  'mapGrid.selectedRegion': { index: null, value: '' },
  'mapGrid.regionIndex': null,
  'mapGrid.originalSectionName': null,
  'mapGrid.originalType': null,
  'mapGrid.sectionName': '',
  'mapGrid.type': 'section',
  'mapGrid.regionType': 'default',
  'mapGrid.clonedValues': {},
  'mapGrid.disableSelect': false,
});

export const MAP_GRID_TILE_KEYS = [
  'tile_ix',
  'tile_iy',
  'tile_pan',
  'tile_tilt',
  'local_x',
  'local_y',
];

const roundTileFloat = (value) => Math.round(Number(value) * 1e6) / 1e6;

/** Grid cell + local pixel for a mosaic pick (for mapping JSON provenance). */
export const mosaicTileMetadata = (grid, mosaicX, mosaicY) => {
  if (!isUsableGrid(grid) || mosaicX == null || mosaicY == null) return null;

  const {
    ix, iy, lx, ly,
  } = mosaicToCell(grid, mosaicX, mosaicY);
  const cell = grid.cells.find((c) => c.ix === ix && c.iy === iy);
  if (!cell) return null;

  return {
    tile_ix: ix,
    tile_iy: iy,
    tile_pan: roundTileFloat(cell.pan),
    tile_tilt: roundTileFloat(cell.tilt),
    local_x: Math.round(lx),
    local_y: Math.round(ly),
  };
};

/**
 * Corners that share the same physical seat (identical row + seat), same as
 * Check tab. A typical mapped section shares a seat *number* on TL↔BL /
 * TR↔BR (same column, different rows) — those must not share pan/tilt or a
 * mosaic pick on TL would also move BL.
 */
export const cornersSharingPtz = (corners, sourceLabel) => {
  const source = corners?.[sourceLabel];
  if (!source) return [sourceLabel];

  const srcRow = String(source.row ?? '').trim().toUpperCase();
  const srcSeat = String(source.seat ?? '').trim();
  const targets = new Set([sourceLabel]);

  if (srcRow === '' || srcSeat === '') return [sourceLabel];

  CORNER_ORDER.forEach((label) => {
    if (label === sourceLabel) return;
    const c = corners?.[label];
    if (!c) return;
    const row = String(c.row ?? '').trim().toUpperCase();
    const seat = String(c.seat ?? '').trim();
    if (row === srcRow && seat === srcSeat) targets.add(label);
  });

  return [...targets];
};

const pickTargets = (corner, regionType, corners) => {
  const source = corner || 'topLeft';
  // Prefer Check-style linking from current row/seat values on the cards.
  const fromRowsSeats = cornersSharingPtz(corners, source);
  if (fromRowsSeats.length > 1) return fromRowsSeats;

  if (regionType === 'singleSeat') return [...CORNER_ORDER];
  if (regionType === 'singleRow') {
    return source.includes('Left')
      ? ['topLeft', 'bottomLeft']
      : ['topRight', 'bottomRight'];
  }
  return fromRowsSeats;
};

/** Redux path updates for a mosaic click. Does not change activeCorner. */
export const mosaicPickPathUpdates = ({
  corner,
  panRel,
  tiltRel,
  mosaicX,
  mosaicY,
  regionType,
  corners,
  grid,
}) => {
  const source = corner || 'topLeft';
  const targets = pickTargets(source, regionType, corners);
  const updates = {};
  const tile = grid ? mosaicTileMetadata(grid, mosaicX, mosaicY) : null;

  targets.forEach((label) => {
    updates[`mapGrid.${label}.pan`] = panRel;
    updates[`mapGrid.${label}.tilt`] = tiltRel;
    updates[`mapGrid.${label}.mosaicX`] = mosaicX;
    updates[`mapGrid.${label}.mosaicY`] = mosaicY;
    MAP_GRID_TILE_KEYS.forEach((key) => {
      updates[`mapGrid.${label}.${key}`] = tile ? tile[key] : null;
    });
  });

  return updates;
};

const unwrapPanDelta = (target, source) => {
  let delta = Number(target) - Number(source);
  while (delta > 1) delta -= 2;
  while (delta < -1) delta += 2;
  return delta;
};

/** Map-file integers (relative to ref) -> absolute ONVIF pan/tilt. */
export const mapRelativeToOnvif = (panRel, tiltRel, reference) => ({
  pan: (Number(panRel) + Number(reference?.pan || 0)) / ONVIF_MULTIPLIER,
  tilt: (Number(tiltRel) + Number(reference?.tilt || 0)) / ONVIF_MULTIPLIER,
});

/** Absolute ONVIF pan/tilt -> mosaic pixel, or null if it is not inside any cell. */
export const ptzToMosaicPixel = (grid, pan, tilt) => {
  if (!isUsableGrid(grid) || pan == null || tilt == null) return null;

  const width = Number(grid.width);
  const height = Number(grid.height);
  const centerX = grid.center_x ?? width / 2;
  const centerY = grid.center_y ?? height / 2;
  const kPan = Number(grid.k_pan_per_pixel) * (grid.pan_apply_scale ?? 1.0);
  const kTilt = Number(grid.k_tilt_per_pixel);
  if (!kPan || !kTilt) return null;

  let best = null;
  let bestScore = Infinity;
  grid.cells.forEach((cell) => {
    const lx = centerX - unwrapPanDelta(pan, cell.pan) / kPan;
    const ly = centerY - (Number(tilt) - Number(cell.tilt)) / kTilt;
    const dx = lx < 0 ? -lx : Math.max(0, lx - width);
    const dy = ly < 0 ? -ly : Math.max(0, ly - height);
    const score = (dx * dx) + (dy * dy);
    if (score < bestScore) {
      bestScore = score;
      best = {
        mosaicX: Number(cell.ix) * width + lx,
        mosaicY: Number(cell.iy) * height + ly,
        inside: score === 0,
      };
    }
  });

  return best?.inside ? best : null;
};

/** Attach tile metadata from mosaic coords or projected PTZ before save. */
const enrichMapGridCorner = (corner, grid, reference) => {
  if (!corner || !isUsableGrid(grid)) return corner;

  let { mosaicX, mosaicY } = corner;
  if ((mosaicX == null || mosaicY == null)
    && corner.pan !== '' && corner.pan != null
    && corner.tilt !== '' && corner.tilt != null) {
    const onvif = mapRelativeToOnvif(corner.pan, corner.tilt, reference);
    const hit = ptzToMosaicPixel(grid, onvif.pan, onvif.tilt);
    if (hit) {
      mosaicX = hit.mosaicX;
      mosaicY = hit.mosaicY;
    }
  }

  const tile = mosaicTileMetadata(grid, mosaicX, mosaicY);
  if (!tile) return corner;

  return { ...corner, ...tile };
};

/**
 * When the mosaic of record changes, refresh tile provenance on corners that
 * already had tile_* (Map Grid picks). Pan/tilt stay. Corners that no longer
 * land on the new mosaic lose tile_* (cannot invent a cell).
 * Returns { updated, cleared }.
 */
export const refreshMapGridTileProvenance = (sections = [], grid, reference) => {
  let updated = 0;
  let cleared = 0;

  sections.forEach((section) => {
    (section?.regions || []).forEach((region) => {
      (region || []).forEach((corner) => {
        if (!corner || typeof corner !== 'object' || Array.isArray(corner)) return;

        const hadTile = MAP_GRID_TILE_KEYS.some((key) => Object.hasOwn(corner, key));
        if (!hadTile) return;

        MAP_GRID_TILE_KEYS.forEach((key) => {
          if (Object.hasOwn(corner, key)) {
            // Mutate cam-map corners in place before upload.
            // eslint-disable-next-line no-param-reassign
            delete corner[key];
          }
        });

        const hasPtz = corner.pan !== '' && corner.pan != null
          && corner.tilt !== '' && corner.tilt != null
          && !Number.isNaN(Number(corner.pan))
          && !Number.isNaN(Number(corner.tilt));
        if (!hasPtz || !isUsableGrid(grid)) {
          cleared += 1;
          return;
        }

        const onvif = mapRelativeToOnvif(corner.pan, corner.tilt, reference);
        const hit = ptzToMosaicPixel(grid, onvif.pan, onvif.tilt);
        const tile = hit ? mosaicTileMetadata(grid, hit.mosaicX, hit.mosaicY) : null;
        if (!tile) {
          cleared += 1;
          return;
        }

        Object.assign(corner, tile);
        updated += 1;
      });
    });
  });

  return { updated, cleared };
};

/** Corner object -> mapping JSON corner, including optional tile fields. */
export const formatMapGridCorner = (corner, position, grid, reference) => {
  const enriched = enrichMapGridCorner(corner, grid, reference);
  const formatted = {
    position,
    row: enriched.row,
    seat: Number.parseInt(enriched.seat, 10),
    pan: Number.parseInt(enriched.pan, 10),
    tilt: Number.parseInt(enriched.tilt, 10),
  };

  MAP_GRID_TILE_KEYS.forEach((key) => {
    const value = enriched[key];
    if (value != null && value !== '') formatted[key] = value;
  });

  return formatted;
};

const colorForSection = (name) => colorForLabel(name, REGION_COLORS);

const cornerByPosition = (raw, position, index) => (
  (raw || []).find((c) => c?.position === position) || raw?.[index] || null
);

const projectCorner = (grid, corner, reference) => {
  if (corner?.pan == null || corner?.tilt == null) return null;
  const { pan, tilt } = mapRelativeToOnvif(corner.pan, corner.tilt, reference);
  return ptzToMosaicPixel(grid, pan, tilt);
};

const toPoints = (list) => list.map((p) => `${p.mosaicX},${p.mosaicY}`).join(' ');

/**
 * Saved mapping regions whose four corners all land on this mosaic, with
 * row/seat lattice lines for the overlay.
 */
export const mappedRegionsOnMosaic = (grid, { sections, rowDict, reference } = {}) => {
  if (!isUsableGrid(grid) || !sections?.length || !rowDict) return [];

  setRowDict(rowDict);
  const dictRows = Object.keys(rowDict).sort((a, b) => rowDict[a] - rowDict[b]);
  const overlay = [];

  sections.forEach((section) => {
    const name = String(section?.name || '').trim() || '?';
    (section.regions || []).forEach((raw, regionIndex) => {
      if (!Array.isArray(raw) || raw.length < 4) return;
      const region = [
        cornerByPosition(raw, 'top_left', 0),
        cornerByPosition(raw, 'top_right', 1),
        cornerByPosition(raw, 'bottom_left', 2),
        cornerByPosition(raw, 'bottom_right', 3),
      ];
      if (region.some((c) => c?.pan == null || c?.tilt == null)) return;

      const outlineHits = region.map((c) => projectCorner(grid, c, reference));
      if (outlineHits.some((hit) => !hit)) return;

      const seatNums = region.map((c) => Number(c.seat)).filter(Number.isFinite);
      const sMin = Math.min(...seatNums);
      const sMax = Math.max(...seatNums);
      if (![sMin, sMax].every(Number.isFinite)) return;

      const regionRows = dictRows.filter((row) => isRowInRegion(row, region));
      const seats = [];
      regionRows.forEach((row) => {
        for (let seat = sMin; seat <= sMax; seat += 1) {
          const hit = projectCorner(grid, calculatePanTilt(row, seat, region), reference);
          if (hit) {
            seats.push({
              row, seat, mosaicX: hit.mosaicX, mosaicY: hit.mosaicY,
            });
          }
        }
      });

      const byRow = new Map();
      const bySeat = new Map();
      seats.forEach((seat) => {
        if (!byRow.has(seat.row)) byRow.set(seat.row, []);
        byRow.get(seat.row).push(seat);
        if (!bySeat.has(seat.seat)) bySeat.set(seat.seat, []);
        bySeat.get(seat.seat).push(seat);
      });

      const rowOrder = new Map(regionRows.map((row, index) => [row, index]));
      const rowLines = [...byRow.entries()]
        .map(([row, list]) => {
          const sorted = [...list].sort((a, b) => a.seat - b.seat);
          if (sorted.length < 2) return null;
          return { id: row, points: toPoints(sorted) };
        })
        .filter(Boolean);
      const seatLines = [...bySeat.entries()]
        .map(([seat, list]) => {
          const sorted = [...list].sort(
            (a, b) => (rowOrder.get(a.row) ?? 0) - (rowOrder.get(b.row) ?? 0),
          );
          if (sorted.length < 2) return null;
          return { id: seat, points: toPoints(sorted) };
        })
        .filter(Boolean);

      let pitch = 0;
      let gaps = 0;
      byRow.forEach((list) => {
        const sorted = [...list].sort((a, b) => a.seat - b.seat);
        for (let i = 1; i < sorted.length; i += 1) {
          if (sorted[i].seat - sorted[i - 1].seat === 1) {
            pitch += Math.hypot(
              sorted[i].mosaicX - sorted[i - 1].mosaicX,
              sorted[i].mosaicY - sorted[i - 1].mosaicY,
            );
            gaps += 1;
          }
        }
      });

      overlay.push({
        id: `${name}#${regionIndex}`,
        section: name,
        color: colorForSection(name),
        outline: toPoints([outlineHits[0], outlineHits[1], outlineHits[3], outlineHits[2]]),
        rowLines,
        seatLines,
        seats,
        labelX: outlineHits.reduce((sum, hit) => sum + hit.mosaicX, 0) / 4,
        labelY: outlineHits.reduce((sum, hit) => sum + hit.mosaicY, 0) / 4,
        pitch: gaps ? pitch / gaps : 0,
      });
    });
  });

  return overlay;
};
